import moment from 'moment';

import {
  CHECK_IDS_BY_VERIFICATION_TYPE,
  VerificationRunCheck,
} from 'api/bitedb/entities/businessVerification.service';
import {
  businessVerificationReportPDF,
  IBusinessVerificationReportData,
} from 'components/Common/EntityTicketPdf';

import {
  buildEntityVerificationReportChecks,
  latestVerificationRun,
  recordFieldValue,
  VERIFICATION_FIELD_LABELS,
  VERIFICATION_RECORD_FIELDS,
  verificationNextTitle,
  VerificationReportCheck,
  verificationShowsEvidence,
  verificationStatusLabel,
} from './entityVerificationReportModel';
import {
  describeVerificationResult,
  entityStatusLabel,
  resolveVerificationPageState,
  VerificationAttempt,
} from './verificationReportSummary';

const SKIP_REASONS: Record<string, string> = {
  no_domain: 'no domain on file',
  no_email: 'no contact email on file',
  no_phone: 'no contact phone on file',
  no_address: 'no address on file',
  no_name_or_registration: 'no name or registration number on file',
  registry_not_covered: 'the registry covers Canadian companies only',
};

const formatCheckedAt = (value: string | null): string =>
  value ? moment(value).format('MMM D, YYYY · h:mm A') : '';

/** The page check labels a run's Verification Checks feed, de-duplicated. */
const labelsFor = (
  runChecks: VerificationRunCheck[],
  checks: VerificationReportCheck[],
): string[] => {
  const ids = new Set(
    runChecks.flatMap(
      (check) => CHECK_IDS_BY_VERIFICATION_TYPE[check.verificationType] || [],
    ),
  );
  return checks
    .filter((check) => ids.has(check.id))
    .map((check) => check.label);
};

const runNotesFor = (
  runChecks: VerificationRunCheck[] | undefined,
  checks: VerificationReportCheck[],
): string[] => {
  if (!runChecks || !runChecks.length) return [];
  const notes: string[] = [];
  const updated = labelsFor(
    runChecks.filter((check) => check.status === 'completed'),
    checks,
  );
  const notUpdated = labelsFor(
    runChecks.filter(
      (check) => check.status === 'failed' || check.status === 'unavailable',
    ),
    checks,
  );
  if (updated.length) notes.push(`Updated: ${updated.join(', ')}.`);
  if (notUpdated.length) {
    notes.push(
      `Not updated (no response, earlier result kept): ${notUpdated.join(
        ', ',
      )}.`,
    );
  }
  const skipped = new Map<string, string>();
  runChecks
    .filter((check) => check.status === 'skipped')
    .forEach((check) => {
      labelsFor([check], checks).forEach((label) => {
        if (!skipped.has(label)) {
          skipped.set(label, SKIP_REASONS[check.reason || ''] || 'not run');
        }
      });
    });
  skipped.forEach((reason, label) =>
    notes.push(`Not run: ${label} — ${reason}.`),
  );
  return notes;
};

export const buildVerificationReportData = ({
  supplier,
  databaseName,
  preparedBy,
  runChecks,
  interruptedAttempt = null,
}: {
  supplier: any;
  databaseName?: string;
  preparedBy?: string;
  runChecks?: VerificationRunCheck[];
  interruptedAttempt?: VerificationAttempt | null;
}): IBusinessVerificationReportData => {
  const checks = buildEntityVerificationReportChecks(supplier || {});
  const lastRunAt = latestVerificationRun(checks);
  const state = resolveVerificationPageState({
    supplier: supplier,
    checks: checks,
    hasPlan: true,
    running: false,
    interruptedAttempt: interruptedAttempt,
  });
  const summary = describeVerificationResult({
    state: state,
    supplier: supplier,
    checks: checks,
    databaseName: databaseName || 'this database',
    lastRunAt: lastRunAt,
    interruptedAttempt: interruptedAttempt,
  });
  const staleIds = interruptedAttempt?.staleCheckIds || [];

  return {
    entityName: supplier?.name || 'Entity',
    databaseName: databaseName,
    country: supplier?.country_of_origin || undefined,
    status: entityStatusLabel(supplier?.status_flag),
    lastVerified: formatCheckedAt(lastRunAt),
    preparedBy: preparedBy,
    figure: summary.figure,
    figureTone: summary.figureTone,
    tail: summary.tail,
    summary: summary.summary,
    counts: {
      passed: checks.filter((check) => check.tone === 'passed').length,
      attention: checks.filter(
        (check) => check.tone === 'review' || check.tone === 'input',
      ).length,
      failed: checks.filter((check) => check.tone === 'failed').length,
    },
    runNotes: runNotesFor(runChecks, checks),
    checks: checks.map((check) => {
      const notUpdated =
        Boolean(interruptedAttempt?.nothingUpdated) ||
        staleIds.includes(check.id);
      return {
        label: check.label,
        tone: check.tone,
        statusLabel: verificationStatusLabel(check, notUpdated),
        description: check.description,
        nextTitle: verificationNextTitle(check),
        next: check.next,
        reads: check.reads.map((field) => ({
          label: VERIFICATION_FIELD_LABELS[field],
          value: recordFieldValue(supplier, field) || 'Not set',
        })),
        evidence: verificationShowsEvidence(check) ? check.details : [],
        checkedAt: formatCheckedAt(check.checkedAt),
        checkedOn: check.checkedAt
          ? moment(check.checkedAt).format('MMM D, YYYY')
          : '',
      };
    }),
    fields: VERIFICATION_RECORD_FIELDS.map((field) => {
      const value = recordFieldValue(supplier, field);
      const readBy = checks.filter((check) => check.reads.includes(field));
      const neededBy = readBy.filter(
        (check) => check.tone === 'input' && check.fix === field,
      );
      return {
        label: VERIFICATION_FIELD_LABELS[field],
        value: value || 'Not set',
        usedBy:
          !value && neededBy.length
            ? `Needed by ${neededBy.map((check) => check.label).join(', ')}`
            : readBy.map((check) => check.label).join(', '),
      };
    }),
  };
};

/** The signed-in user's name, for the report's "Prepared by" row. */
export const reportPreparedBy = (
  user?: { basics?: { firstName?: string; lastName?: string } } | null,
): string =>
  [user?.basics?.firstName, user?.basics?.lastName].filter(Boolean).join(' ');

/** "Business Verification - Acme Ltd - 2026-10-02.pdf", safe on every OS. */
export const verificationReportFileName = (entityName: string): string =>
  `Business Verification - ${
    String(entityName || 'Entity')
      .replace(/[\\/:*?"<>|]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 80) || 'Entity'
  } - ${moment().format('YYYY-MM-DD')}.pdf`;

export const downloadVerificationReport = (
  options: Parameters<typeof buildVerificationReportData>[0],
): Promise<Blob> => {
  const data = buildVerificationReportData(options);
  return businessVerificationReportPDF(
    data,
    verificationReportFileName(data.entityName),
  );
};
